import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import ComposeEmail from "@/components/ComposeEmail";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { badgeClass } from "@/lib/config";
import { Mail, Plus, RefreshCw, XCircle } from "lucide-react";

const TABS = ["All", "Draft", "Queued", "Sending", "Accepted by Provider", "Failed", "Retry Scheduled", "Cancelled"];

export default function EmailCenter() {
  const { can } = useAuth();
  const [tab, setTab] = useState("All");
  const [jobs, setJobs] = useState([]);
  const [status, setStatus] = useState(null);
  const [compose, setCompose] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/email/jobs${tab !== "All" ? `?status=${encodeURIComponent(tab)}` : ""}`);
      setJobs(data.items || []);
      const s = await api.get("/email/status"); setStatus(s.data);
    } catch { toast.error("Could not load email."); }
  }, [tab]);
  useEffect(() => { load(); const t = setInterval(load, 5000); return () => clearInterval(t); }, [load]);

  const retry = async (j) => { try { await api.post(`/email/jobs/${j.id}/retry`); toast.success("Re-queued."); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const cancel = async (j) => { try { await api.post(`/email/jobs/${j.id}/cancel`); toast.success("Cancelled."); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const sendDraft = async (j) => { try { await api.post(`/email/jobs/${j.id}/send`); toast.success("Queued."); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const openDetail = async (j) => { const { data } = await api.get(`/email/jobs/${j.id}`); setDetail(data); };

  return (
    <div className="p-6 animate-fade-in">
      <PageHeader title="Email Center" subtitle="Durable send queue via Proton. SMTP acceptance is not the same as inbox delivery.">
        {can.write && <Button data-testid="email-compose-open" onClick={() => setCompose(true)} className="bg-navy hover:bg-atlantic text-white"><Plus className="h-4 w-4 mr-1.5" /> Compose</Button>}
      </PageHeader>

      {status && (
        <div className="bg-white border border-border rounded-sm p-3 mb-4 flex flex-wrap items-center gap-4 text-sm" data-testid="email-status-bar">
          <span className="flex items-center gap-1.5"><Mail className="h-4 w-4 text-atlantic" /> {status.provider}</span>
          <Badge variant="outline" className={status.configured ? "bg-seagreen/10 text-seagreen border-seagreen/30" : "bg-seaamber/10 text-seaamber border-seaamber/30"} data-testid="email-health">{status.label}</Badge>
          <span className="text-mutedsea">From: {status.from_address}</span>
          <span className="text-mutedsea">Queue: {status.queue_depth}</span>
          <span className="text-mutedsea">Failed: {status.failed_count}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-1 mb-4 border-b border-border">
        {TABS.map((t) => (
          <button key={t} data-testid={`email-tab-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setTab(t)}
            className={`px-3 py-2 text-sm nav-link -mb-px border-b-2 ${tab === t ? "border-atlantic text-navy font-medium" : "border-transparent text-mutedsea hover:text-slate"}`}>{t}</button>
        ))}
      </div>

      {jobs.length === 0 ? (
        <div className="bg-white border border-border rounded-sm p-10 text-center text-mutedsea" data-testid="email-empty"><Mail className="h-8 w-8 mx-auto mb-2 text-sky" /> No messages.</div>
      ) : (
        <div className="bg-white border border-border rounded-sm overflow-hidden" data-testid="email-list">
          <table className="w-full text-sm">
            <thead className="bg-cloud text-mutedsea text-xs uppercase tracking-wide">
              <tr><th className="text-left px-3 py-2">To</th><th className="text-left px-3 py-2">Subject</th><th className="text-left px-3 py-2">Status</th><th className="text-left px-3 py-2">When</th><th className="text-left px-3 py-2">Actions</th></tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} data-testid={`email-row-${j.id}`} className="border-t border-border hover:bg-ice">
                  <td className="px-3 py-2 text-slate cursor-pointer" onClick={() => openDetail(j)}>{(j.to || []).join(", ")}</td>
                  <td className="px-3 py-2 text-slate cursor-pointer" onClick={() => openDetail(j)}>{j.subject}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className={badgeClass(j.status === "Accepted by Provider" ? "active" : j.status)}>{j.status}</Badge></td>
                  <td className="px-3 py-2 text-mutedsea text-xs">{(j.sent_at || j.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-2 flex gap-1">
                    {can.write && j.status === "Draft" && <Button size="sm" data-testid={`email-send-${j.id}`} onClick={() => sendDraft(j)} className="bg-navy text-white h-7 text-xs">Send</Button>}
                    {can.write && ["Failed", "Retry Scheduled"].includes(j.status) && <Button size="sm" variant="outline" data-testid={`email-retry-${j.id}`} onClick={() => retry(j)} className="border-atlantic text-atlantic h-7 text-xs"><RefreshCw className="h-3 w-3 mr-1" />Retry</Button>}
                    {can.write && ["Draft", "Queued", "Retry Scheduled"].includes(j.status) && <Button size="sm" variant="outline" data-testid={`email-cancel-${j.id}`} onClick={() => cancel(j)} className="border-seared text-seared h-7 text-xs"><XCircle className="h-3 w-3 mr-1" />Cancel</Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ComposeEmail open={compose} onOpenChange={setCompose} onSent={load} />

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-xl bg-white" data-testid="email-detail-dialog">
          <DialogHeader><DialogTitle className="font-serif text-xl text-navy">{detail?.subject}</DialogTitle></DialogHeader>
          {detail && (
            <div className="text-sm space-y-2">
              <div className="text-xs text-mutedsea">To: {(detail.to || []).join(", ")} {detail.cc?.length ? ` · Cc: ${detail.cc.join(", ")}` : ""}</div>
              <Badge variant="outline" className={badgeClass(detail.status === "Accepted by Provider" ? "active" : detail.status)}>{detail.status}</Badge>
              <div className="bg-cloud rounded-sm p-2 whitespace-pre-wrap text-slate max-h-40 overflow-y-auto">{detail.body}</div>
              {detail.attachments?.length > 0 && <div className="text-xs text-mutedsea">Attachments: {detail.attachments.map((a) => a.filename + (a.edition ? ` (${a.edition})` : "")).join(", ")}</div>}
              <div className="border-t border-border pt-2">
                <div className="text-xs font-semibold text-slate mb-1">Activity timeline</div>
                {(detail.activity || []).map((a, i) => <div key={i} className="text-xs text-mutedsea">{a.at?.slice(0, 19).replace("T", " ")} — {a.event}</div>)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
